var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { createContext, useContext, useEffect, useMemo, useState, } from 'react';
import { useOpts } from '../context';
import { useGetLocalizedString } from './useGetLocalizedString';
export var sandboxContext = createContext({
    hint: undefined,
    setFields: function () { },
    prefilledFormData: {},
});
var HINT_MAP = {
    email: 'user_good@example.com',
    company: 'company_good',
    username: 'user_good',
    pin: '1234',
    postal_code: '12345',
    number: '1234567890',
    social_security_number: '123456789',
    password: 'pass_good',
    mfa: 'mfa_code',
    initials: 'abc',
    employee_id: 'user_good',
};
var PREFILL_FIELD_MAP = __assign(__assign({}, HINT_MAP), { 
    // Use a non-compromised password for demo, so Android phones don't show a data breach
    // warning when using `demo_pass`
    password: 'demo_pass!12358' });
function getSandboxKey(key) {
    if (key.includes('initials'))
        return 'initials';
    if (key.includes('password'))
        return 'password';
    if (key.includes('mfa_code'))
        return 'mfa';
    if (key.includes('email'))
        return 'email';
    if (key.includes('company'))
        return 'company';
    if (key.includes('user'))
        return 'username';
    if (key.includes('employee_id'))
        return 'employee_id';
    if (key.includes('employee'))
        return 'username';
    if (key.includes('last_four_ssn'))
        return 'pin';
    if (key.includes('pin'))
        return 'pin';
    if (key.includes('postal_code'))
        return 'postal_code';
    if (key.includes('social_security_number'))
        return 'social_security_number';
    if (key.includes('number') ||
        key.includes('Number') ||
        key.includes('phone') ||
        key.includes('Phone'))
        return 'number';
    return null;
}
export function getPrefilledField(field) {
    if (field.fieldType === 'select_single' ||
        field.fieldType === 'toggleButtonGroup') {
        return field.options[0];
    }
    return PREFILL_FIELD_MAP[getSandboxKey(field.key)];
}
var SandboxProvider = function (_a) {
    var children = _a.children;
    var getLocalizedString = useGetLocalizedString();
    var _b = useOpts(), linkTokenPayload = _b.linkTokenPayload, isDemoMode = _b.isDemoMode;
    var _c = useState([]), fields = _c[0], setFields = _c[1];
    var hint = fields
        .reduce(function (acc, curr) {
        var sandboxKey = getSandboxKey(curr.key);
        return sandboxKey
            ? acc.concat("".concat(curr.key, ": ").concat(HINT_MAP[sandboxKey]))
            : acc;
    }, [])
        .join(' | ');
    var shouldDisplayHint = hint && linkTokenPayload.mode === 'sandbox' && !isDemoMode;
    var shouldPrefillForms = linkTokenPayload.mode === 'sandbox' && isDemoMode;
    var contextValue = useMemo(function () { return ({
        setFields: setFields,
        hint: shouldDisplayHint
            ? "".concat(getLocalizedString('SANDBOX_MODE'), " \u00BB ").concat(hint)
            : undefined,
        prefilledFormData: shouldPrefillForms
            ? Object.fromEntries(fields.map(function (curr) { return [curr.key, getPrefilledField(curr)]; }))
            : {},
    }); }, [getLocalizedString, hint, shouldDisplayHint, fields, shouldPrefillForms]);
    return (React.createElement(sandboxContext.Provider, { value: contextValue }, children));
};
/**
 *
 * @param fields - optionally set fields if they are passed in
 * @returns object - { hint: string }
 */
export var useSandbox = function (fields) {
    var ctx = useContext(sandboxContext);
    useEffect(function () {
        if (fields)
            ctx.setFields(fields);
    }, [ctx, fields]);
    return { hint: ctx.hint, prefilledFormData: ctx.prefilledFormData };
};
export default SandboxProvider;
